import { Injectable, signal } from '@angular/core';
import { fromOffice } from '../from-office';
import { FiledTarget, MailAddress } from './mail-upload.models';

/** Which message the taskpane is about, as far as the UI needs to know. */
export interface OpenMail {
  itemId: string | null;
  subject: string;
  /**
   * The address matching Geschäfte are proposed for: the sender, or for a
   * mail the user sent, its first recipient. Null without an address.
   */
  counterpart: string | null;
}

const EWS_TYPES_NAMESPACE = 'http://schemas.microsoft.com/exchange/services/2006/types';

/** The Outlook category a filed mail carries, so CMI mails stand out in the message list. */
export const CMI_CATEGORY = 'CMI';

/** The custom property on a filed mail that records where it went, newest first. */
export const FILINGS_PROPERTY = 'cmiFilings';

/** A record that is missing or unreadable reads as a mail never filed. */
export function parseFilings(value: unknown): FiledTarget[] {
  if (typeof value !== 'string') {
    return [];
  }

  try {
    const filings: unknown = JSON.parse(value);

    return Array.isArray(filings) ? (filings as FiledTarget[]) : [];
  } catch {
    return [];
  }
}

/** The Date header of a mail's raw headers as ISO, or null when it is missing or unreadable. */
export function dateHeader(headers: string): string | null {
  // A long header goes on in lines that start with whitespace.
  const match = /^date:[ \t]*(.*(?:\r?\n[ \t].*)*)/im.exec(headers);
  const date = match ? new Date(match[1].replace(/\r?\n[ \t]+/g, ' ').trim()) : null;

  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
}

/** The endpoint types absender as a non-nullable object, so never hand it null. */
function toMailAddress(details: Office.EmailAddressDetails | undefined): MailAddress {
  return {
    name: details?.displayName ?? '',
    email: details?.emailAddress ?? ''
  };
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// No <?xml ...?> declaration on purpose: makeEwsRequestAsync rejects a request
// that declares UTF-8 encoding, and older Outlook builds want ISO-8859-1. Leaving
// the declaration out altogether is the one form every client accepts.
function buildGetItemRequest(itemId: string): string {
  return `<soap:Envelope
    xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:t="${EWS_TYPES_NAMESPACE}">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013" />
  </soap:Header>
  <soap:Body>
    <m:GetItem xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
      <m:ItemShape>
        <t:BaseShape>IdOnly</t:BaseShape>
        <t:IncludeMimeContent>true</t:IncludeMimeContent>
      </m:ItemShape>
      <m:ItemIds>
        <t:ItemId Id="${escapeXml(itemId)}" />
      </m:ItemIds>
    </m:GetItem>
  </soap:Body>
</soap:Envelope>`;
}

/**
 * Reads the message currently open in Outlook as a base64 RFC-822 EML.
 *
 * Two paths, picked by what the running client actually offers rather than by
 * the manifest minimum:
 *
 * - getAsFileAsync (mailbox requirement set 1.14) wherever it exists. This is
 *   the only one that works against Microsoft 365, where the legacy Exchange
 *   tokens behind makeEwsRequestAsync have been turned off tenant-wide.
 * - EWS GetItem with IncludeMimeContent otherwise, which keeps Exchange
 *   on-premises working: it caps the add-in at requirement set 1.5, so
 *   getAsFileAsync is not there at all.
 *
 * Both hand back base64 already, so neither needs re-encoding.
 */
@Injectable({
  providedIn: 'root'
})
export class MailService {
  private readonly _openMail = signal<OpenMail>(this._readOpenMail());

  /**
   * The message the taskpane is currently about.
   *
   * A pinned taskpane outlives the message it was opened for: Outlook swaps
   * `Office.context.mailbox.item` underneath it and raises `ItemChanged`. Every
   * decision in the pane was made about the message that was open at the time,
   * so consumers watch this and start over when it changes.
   */
  public readonly openMail = this._openMail.asReadonly();

  public constructor() {
    // addHandlerAsync arrived with requirement set 1.5, the same set that
    // brought pinning. A client without it cannot pin the pane, so it closes on
    // an item change instead of reporting one.
    if (typeof Office.context.mailbox.addHandlerAsync !== 'function') {
      return;
    }

    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      (): void => this._openMail.set(this._readOpenMail()),
      (result: Office.AsyncResult<void>): void => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error('Listening for the selected mail failed:', result.error);
        }
      }
    );
  }

  public getCurrentMailSubject(): string {
    return this._item()?.subject ?? '';
  }

  /**
   * Read straight off Office rather than from {@link openMail}, so a caller
   * checking whether the mail moved does not depend on the event having been
   * delivered yet.
   */
  public getCurrentItemId(): string | null {
    return this._item()?.itemId ?? null;
  }

  /**
   * Sender, recipients and send time of the open message, as the filing
   * endpoint wants them. Read-mode properties, so no async round trip.
   */
  public getCurrentMailMetadata(): {
    absender: MailAddress;
    empfaenger: MailAddress[];
    cc: MailAddress[];
    gesendetDatum: string | null;
  } {
    const item = this._item();

    return {
      absender: toMailAddress(item?.from),
      empfaenger: (item?.to ?? []).map(toMailAddress),
      cc: (item?.cc ?? []).map(toMailAddress),
      gesendetDatum: item?.dateTimeCreated?.toISOString() ?? null
    };
  }

  /**
   * When the sender sent the open message, from its Date header: read mode
   * offers no send time of its own. The headers need requirement set 1.8,
   * which Exchange on-premises never reaches, so null there and on any failure.
   * Never rejects.
   */
  public async getCurrentMailSentDateTime(): Promise<string | null> {
    const item = this._item();

    if (typeof item?.getAllInternetHeadersAsync !== 'function') {
      return null;
    }

    try {
      return dateHeader(
        await fromOffice<string>((callback): void => item.getAllInternetHeadersAsync(callback))
      );
    } catch (error) {
      console.warn('Reading the headers of the mail failed, it has no send time:', error);
      return null;
    }
  }

  /**
   * Marks the open message with the CMI category, so the message list shows at a
   * glance which mails are already filed.
   *
   * Categories need mailbox requirement set 1.8, which the Exchange
   * on-premises clients this add-in also serves never reach — the same divide as
   * getAsFileAsync above. There the mark is simply absent.
   *
   * Acts on whatever Outlook has selected rather than on an item id, so a caller
   * with a pinned pane has to establish first that the filed mail is still open.
   * From there this holds on to the mail it started on and marks nothing if
   * Outlook moves on mid-call.
   */
  public async markCurrentMailAsFiled(): Promise<void> {
    const itemId = this.getCurrentItemId();

    if (itemId === null || !Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
      return;
    }

    const category = await this.ensureCmiCategory();
    // Reading the master list is a server round trip, and a pinned pane can move
    // to the next mail across it, Outlook swapping the item object as it goes.
    // So the item is taken on this side of it, and only if it is still the one
    // the caller filed.
    const item = this._item();

    if (!item || item.itemId !== itemId) {
      return;
    }

    await fromOffice<void>((callback): void => item.categories.addAsync([category], callback));
  }

  /**
   * Where the open message has been filed so far, newest first, as its custom
   * properties record it. Empty for a mail never filed, and where no message is
   * open.
   */
  public async getCurrentMailFilings(): Promise<FiledTarget[]> {
    const item = this._item();

    if (!item) {
      return [];
    }

    const properties = await fromOffice<Office.CustomProperties>((callback): void =>
      item.loadCustomPropertiesAsync(callback)
    );

    return parseFilings(properties.get(FILINGS_PROPERTY));
  }

  /**
   * Adds a filing to the open message's record, so the pane can show it again
   * when the mail is reopened. Custom properties are there from requirement
   * set 1.1, so unlike the category this works on Exchange on-premises too.
   *
   * The same caveat as {@link markCurrentMailAsFiled}: the caller establishes
   * that the filed mail is still open, and nothing is written if Outlook moves
   * on while the properties load.
   */
  public async recordCurrentMailFiling(target: FiledTarget): Promise<void> {
    const item = this._item();

    if (!item) {
      return;
    }

    const itemId = item.itemId;
    const properties = await fromOffice<Office.CustomProperties>((callback): void =>
      item.loadCustomPropertiesAsync(callback)
    );

    if (this.getCurrentItemId() !== itemId) {
      return;
    }

    const filings = [target, ...parseFilings(properties.get(FILINGS_PROPERTY))];
    properties.set(FILINGS_PROPERTY, JSON.stringify(filings));

    await fromOffice<void>((callback): void => properties.saveAsync(callback));
  }

  /**
   * The category to mark with, put into the mailbox's master list first: Office
   * refuses a category on an item that the list does not carry, and refuses a
   * second copy in the list with DuplicateCategory.
   *
   * Reports the name as the list spells it. Outlook's uniqueness ignores case,
   * so a mailbox where mails were tagged "cmi" by hand reuses that one instead
   * of asking for a duplicate it would never get.
   *
   * Public for batch filing, which marks the mails through Graph but keeps
   * the master list to Office.js, once per batch.
   */
  public async ensureCmiCategory(): Promise<string> {
    const masterCategories = await fromOffice<Office.CategoryDetails[]>((callback): void =>
      Office.context.mailbox.masterCategories.getAsync(callback)
    );
    const existing = masterCategories.find(
      (category) => category.displayName.toLowerCase() === CMI_CATEGORY.toLowerCase()
    );

    if (existing) {
      return existing.displayName;
    }

    await fromOffice<void>((callback): void =>
      Office.context.mailbox.masterCategories.addAsync(
        // Preset7 is the palette's plain "Blue", which renders light blue; the
        // only other blue the enum offers is Preset22, the navy "DarkBlue".
        [{ displayName: CMI_CATEGORY, color: Office.MailboxEnums.CategoryColor.Preset7 }],
        callback
      )
    );

    return CMI_CATEGORY;
  }

  /** None in the no-item context: nothing selected, or several mails at once. */
  private _item(): Office.MessageRead | null {
    return (Office.context.mailbox.item as Office.MessageRead | null | undefined) ?? null;
  }

  /** A pinned pane can sit over an empty reading pane, where there is no item. */
  private _readOpenMail(): OpenMail {
    return {
      itemId: this.getCurrentItemId(),
      subject: this.getCurrentMailSubject(),
      counterpart: this._readCounterpart()
    };
  }

  /** See {@link OpenMail.counterpart}. */
  private _readCounterpart(): string | null {
    const item = this._item();
    const sender = item?.from?.emailAddress ?? '';
    const ownAddress = Office.context.mailbox.userProfile?.emailAddress ?? '';
    const address =
      sender.toLowerCase() === ownAddress.toLowerCase()
        ? (item?.to?.[0]?.emailAddress ?? '')
        : sender;

    return address === '' ? null : address;
  }

  public getCurrentMailEmlBase64(): Promise<string> {
    return this._canGetAsFile() ? this._readAsFile() : this._readViaEws();
  }

  private _canGetAsFile(): boolean {
    return (
      Office.context.requirements.isSetSupported('Mailbox', '1.14') &&
      typeof Office.context.mailbox.item?.getAsFileAsync === 'function'
    );
  }

  private _readAsFile(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      Office.context.mailbox.item?.getAsFileAsync((result: Office.AsyncResult<string>): void => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'Reading the message failed'));
          return;
        }

        resolve(result.value);
      });
    });
  }

  private _readViaEws(): Promise<string> {
    const itemId = Office.context.mailbox.item?.itemId;

    if (!itemId) {
      return Promise.reject(new Error('No message is open in Outlook'));
    }

    return new Promise<string>((resolve, reject) => {
      Office.context.mailbox.makeEwsRequestAsync(
        buildGetItemRequest(itemId),
        (result: Office.AsyncResult<string>): void => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            reject(new Error(result.error?.message ?? 'The EWS request failed'));
            return;
          }

          const mimeContent = this._readMimeContent(result.value);

          if (mimeContent === null) {
            reject(new Error('The EWS response carried no MIME content'));
            return;
          }

          resolve(mimeContent);
        }
      );
    });
  }

  private _readMimeContent(responseXml: string): string | null {
    const document = new DOMParser().parseFromString(responseXml, 'text/xml');
    const mimeContent = document.getElementsByTagNameNS(EWS_TYPES_NAMESPACE, 'MimeContent')[0];
    const content = mimeContent?.textContent?.trim();

    return content ? content : null;
  }
}
